/**
 * Review Progress Indicator - Shows FunnelAssist review status for funnel
 * 
 * Displays:
 * - "Step Review Progress: X of Y steps reviewed"
 * - Consent text status
 * - List of unreviewed steps
 * - Publish blocker warnings
 */

import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronDown,
  Sparkles,
  FileText
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function ReviewProgressIndicator({
  stats,
  onNavigateToStep,
  className
}) {
  if (!stats || stats.ai_generated === 0) {
    // No AI-generated content
    return null;
  }
  
  const {
    total_nodes = 0,
    ai_generated = 0,
    reviewed = 0,
    unreviewed = 0,
    consent_text_entered = true,
    can_publish = false,
    unreviewed_nodes = []
  } = stats;
  
  const progressPercent = ai_generated > 0 
    ? Math.round((reviewed / ai_generated) * 100)
    : 100;
  
  const allReviewed = reviewed === ai_generated;
  const hasIssues = !allReviewed || !consent_text_entered;
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "gap-2",
            hasIssues 
              ? "border-amber-500/30 text-amber-400 hover:border-amber-500/50" 
              : "border-green-500/30 text-green-400 hover:border-green-500/50",
            className
          )}
          data-testid="review-progress-btn"
        >
          {hasIssues ? (
            <AlertTriangle className="h-4 w-4" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">
            Step Review Progress: {reviewed} of {ai_generated} steps reviewed
          </span>
          <span className="sm:hidden">
            {reviewed}/{ai_generated} reviewed
          </span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      
      <PopoverContent align="end" className="w-80">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-400" />
            <span className="font-medium text-white">FunnelAssist Review Progress</span>
          </div>
          
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-white/60">
              <span>Steps reviewed</span>
              <span>{reviewed} of {ai_generated}</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>
          
          {/* Issues */}
          {hasIssues && (
            <div className="space-y-2">
              {unreviewed > 0 && (
                <Alert className="py-2 bg-amber-500/10 border-amber-500/30">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <AlertDescription className="text-xs text-amber-200">
                    {unreviewed} step{unreviewed > 1 ? 's' : ''} need{unreviewed === 1 ? 's' : ''} review before publishing
                  </AlertDescription>
                </Alert>
              )}
              
              {!consent_text_entered && (
                <Alert className="py-2 bg-red-500/10 border-red-500/30">
                  <XCircle className="h-4 w-4 text-red-400" />
                  <AlertDescription className="text-xs text-red-200">
                    Consent language must be entered manually
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          
          {/* Unreviewed Steps List */}
          {unreviewed_nodes.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-white/50 font-medium">Needs review:</p>
              {unreviewed_nodes.slice(0, 5).map((node) => (
                <button
                  key={node.id}
                  className={cn(
                    "w-full flex items-center gap-2 p-2 rounded text-left text-sm",
                    "bg-white/5 hover:bg-white/10 transition-colors"
                  )}
                  onClick={() => onNavigateToStep?.(node.id)}
                >
                  <Badge variant="outline" className="text-[10px] px-1.5">
                    {node.node_type}
                  </Badge>
                  <span className="text-white/70 truncate flex-1">
                    {node.label}
                  </span>
                  <span className="text-[10px] text-amber-400">review</span>
                </button>
              ))}
              {unreviewed_nodes.length > 5 && (
                <p className="text-xs text-white/40 text-center">
                  +{unreviewed_nodes.length - 5} more
                </p>
              )}
            </div>
          )}
          
          {/* Status */}
          <div className="pt-2 border-t border-white/10">
            {can_publish ? (
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm">Ready to publish</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-amber-400">
                <XCircle className="h-4 w-4" />
                <span className="text-sm">Cannot publish yet</span>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Inline review badge for individual nodes
 */
export function NodeReviewBadge({ node }) {
  const metadata = node?.metadata || {};
  
  if (!metadata.ai_generated) {
    return null;
  }
  
  if (metadata.ai_reviewed) {
    return (
      <Badge 
        variant="outline" 
        className="text-[10px] px-1.5 text-green-400 border-green-500/30"
      >
        <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
        reviewed
      </Badge>
    );
  }
  
  return (
    <Badge 
      variant="outline" 
      className="text-[10px] px-1.5 text-amber-400 border-amber-500/30 animate-pulse"
    >
      needs review
    </Badge>
  );
}

/**
 * Consent text warning component
 */
export function ConsentTextWarning({ node, onEdit }) {
  if (node?.node_type !== "consent") return null;
  
  const hasConsentText = !!node?.content?.consent_text;
  
  if (hasConsentText) return null;
  
  return (
    <Alert className="mt-4 bg-red-500/10 border-red-500/30">
      <FileText className="h-4 w-4 text-red-400" />
      <AlertDescription className="text-red-200">
        <p className="font-medium mb-1">Consent Language Required</p>
        <p className="text-xs text-red-200/70">
          AI cannot generate consent/TCPA language. Please enter your approved consent text manually.
          This is required before publishing.
        </p>
        {onEdit && (
          <Button 
            size="sm" 
            variant="outline" 
            className="mt-2 text-red-400 border-red-500/30 hover:border-red-500/50"
            onClick={onEdit}
          >
            Enter Consent Text
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}
